<template>
  <div id="product-detail-page" class="min-vh-100 d-flex flex-column">
    <loading-component-s2 v-if="isLoading" />
    
    <main class="flex-grow-1">
      <!-- Product Not Found -->
      <section v-if="error" class="container oversized-container py-5 text-center">
        <h1 class="h2 font-weight-bold mb-4">Product Not Found</h1>
        <router-link to="/" class="btn btn-accent">
          <i class="fa-solid fa-arrow-left mr-2"></i>
          Return to Home
        </router-link>
      </section>

      <!-- Product Detail -->
      <template v-else>
        <section class="product-header py-4 py-md-5 bg-products">
          <div class="container oversized-container">
            <router-link
              to="/products?k=&c=&b=&s=name&page=1"
              class="d-inline-flex align-items-center text-muted small mb-0 text-decoration-none"
            >
              <i class="fa-solid fa-arrow-left mr-2"></i>
              Back to Products
            </router-link>
          </div>
        </section>

        <section class="container oversized-container py-4 py-md-5">
          <div class="row">
            <!-- Product Image -->
            <div class="col-12 col-md-6 mb-4 mb-md-0">
              <div class="position-relative rounded overflow-hidden bg-light">
                <img
                  :src="product.images[0] || 'public/images/pages/products/placeholder.svg'"
                  class="img-fluid"
                  style="object-fit: cover;"
                />
                <!-- <span
                  v-if="product.inStock"
                  class="badge badge-accent position-absolute"
                  style="top: 1rem; right: 1rem;"
                >
                  In Stock
                </span> -->
              </div>
            </div>

            <!-- Product Info -->
            <div class="col-12 col-md-6">
              <h1 class="h3 h4-md font-weight-bold mb-3">{{ product.name }}</h1>

              <div class="product-sku mb-4" v-if="product.sku">
                <span class="product-sku-label">Part Number</span>
                <span class="product-sku-value">{{ product.sku }}</span>
              </div>

              <div class="small text-muted mb-3" v-if="product.oem_cross_reference">
                <span class="font-weight-medium">OEM Cross Reference:</span> {{ product.oem_cross_reference }}
              </div>

              <div class="box-description mb-3" v-if="product.description" v-html="product.description"></div>

              <p class="text-muted mb-4" v-if="product.short_description" style="line-height: 1.6;">
                {{ product.short_description }}
              </p>

              <div class="d-flex flex-wrap mb-4" v-if="product.engine_application || product.brand || product.category">
                <span v-if="product.engine_application" class="badge badge-accent mr-2 mb-2">
                  {{ product.engine_application }}
                </span>
                <span v-if="product.brand" class="badge badge-secondary mr-2 mb-2">
                  {{ product.brand }}
                </span>
                <span v-if="product.category" class="badge badge-secondary mr-2 mb-2">
                  {{ product.category }}
                </span>
              </div>

              <!-- <div class="d-block align-items-baseline" style="line-height: 1.0;" v-if="product.price_offer">
                <span class="h3 font-weight-bold mr-2 small text-muted text-decoration-line-through">
                  ${{ Intl.NumberFormat("en-US", { minimumFractionDigits: 2 }).format(product.price) }}
                </span>
                <span class="text-muted text-decoration-line-through">USD</span>
              </div> -->

              <div class="d-flex align-items-baseline mb-4" style="flex-wrap: wrap;" v-if="$root.logged && product.distributor">
                <h6 class="title-distributor"><i class="fa-sharp fa-solid fa-box mr-1 ic"></i> Distributor price</h6>
                <span class="h3 text-danger font-weight-bold mr-1">
                  ${{ Intl.NumberFormat("en-US", { minimumFractionDigits: 2 }).format(product.distributor_unit_price) }}
                </span>
                <span class="f-w-700 usd text-danger">USD</span>
                <span class="small text-muted w-100">1 piece</span>
                <p
                  class="small wholesale-distributor mb-0 w-100"
                  v-if="product.wholesale_price && product.wholesale_quantity"
                >
                  Wholesale: ${{ Intl.NumberFormat("en-US", { minimumFractionDigits: 2 }).format(product.wholesale_price) }} USD from {{ product.wholesale_quantity }} {{ product.wholesale_quantity === 1 ? 'piece' : 'pieces' }}
                </p>
              </div>
              <div class="d-flex align-items-baseline mb-4" style="flex-wrap: wrap;" v-else-if="$root.logged">
                <span class="h3 text-danger font-weight-bold mr-1" v-if="product.price_offer">
                  ${{ Intl.NumberFormat("en-US", { minimumFractionDigits: 2 }).format(product.price_offer) }}
                </span>
                <span class="h3 font-weight-bold mr-1" v-if="!product.price_offer">
                  ${{ Intl.NumberFormat("en-US", { minimumFractionDigits: 2 }).format(product.price) }}
                </span>
                <span class="f-w-700 usd" :class="{ 'text-danger': product.price_offer }">USD</span>
              </div>

              <div class="d-flex flex-column flex-sm-row mb-4">
                <!-- <button
                  type="button"
                  class="btn btn-accent btn-lg flex-grow-1 mb-2 mb-sm-0 mr-sm-2"
                >
                  <i class="fa-solid fa-cart-shopping mr-2"></i>
                  Add to Cart
                </button> -->
                <router-link
                  to="/contact"
                  class="btn btn-outline-secondary btn-lg flex-grow-1"
                >
                  Contact Sales
                </router-link>
              </div>

              <!-- Features -->
              <div class="border-top pt-4 mb-4">
                <div class="d-flex align-items-start mb-3">
                  <i class="fa-solid fa-truck text-accent mt-1 mr-3" style="font-size: 1.25rem;"></i>
                  <div>
                    <p class="font-weight-medium mb-0">Fast Shipping</p>
                    <p class="small text-muted mb-0">Ships within 2-3 business days</p>
                  </div>
                </div>
                <div class="d-flex align-items-start mb-3">
                  <i class="fa-solid fa-shield-halved text-accent mt-1 mr-3" style="font-size: 1.25rem;"></i>
                  <div>
                    <p class="font-weight-medium mb-0">Quality Guarantee</p>
                    <p class="small text-muted mb-0">12-month warranty on all parts</p>
                  </div>
                </div>
                <div class="d-flex align-items-start">
                  <i class="fa-solid fa-clock text-accent mt-1 mr-3" style="font-size: 1.25rem;"></i>
                  <div>
                    <p class="font-weight-medium mb-0">Expert Support</p>
                    <p class="small text-muted mb-0">Technical assistance available</p>
                  </div>
                </div>
              </div>

              <!-- Specifications -->
              <!-- <div class="border-top pt-4" v-if="product.description">
                <h2 class="h5 font-weight-bold mb-3">Specifications</h2>
                <div class="box-description" v-html="product.description"></div>
              </div> -->
            </div>
          </div>

          <!-- Related Products -->
          <div  class="border-top pt-5 mt-5">
            <h2 class="h4 font-weight-bold mb-4">Related Products</h2>
            <div class="row">
              <div class="col-12">
                <swiper class="swiper related-swiper" :options="relatedProductsOptions">
                  <swiper-slide v-for="(product, rpInx) in relatedProducts" :key="'rpInx-'+rpInx">
                    <div class="col-12 px-0 box-product-s1">
                      <router-link
                        :to="'/product/'+$root._converToURL(product.name, product.id)"
                        class="text-decoration-none text-dark"
                      >
                        <div class="card h-100 sale-product-card shadow-sm">
                          <div class="position-relative overflow-hidden sale-product-img-wrap">
                            <img
                              src="public/images/pages/products/product.png"
                              class="card-img-top sale-product-img placed-backg"
                              :style="{ backgroundImage: 'url('+ (product.image ? product.image : 'public/images/pages/products/placeholder.svg') +')' }"
                            />
                            <!-- <span class="badge badge-danger position-absolute sale-discount-badge" v-if="product.price_offer">
                              -{{ $root.getPercentDiscount(product.price, product.price_offer) }}%
                            </span> -->
                            <span
                              v-if="product.inStock"
                              class="badge badge-accent position-absolute sale-stock-badge"
                            >
                              In stock
                            </span>
                            <!-- <span class="badge badge-danger position-absolute sale-stock-badge" v-else>Agotado</span> -->
                          </div>
                          <div class="card-body">
                            <div class="d-flex align-items-center mb-2">
                              <span class="text-muted small font-weight-medium mr-2">{{ product.brand }}</span>
                              <span class="text-muted small mr-2">•</span>
                              <span class="text-muted small">{{ product.category }}</span>
                            </div>
                            <h3 class="card-title font-weight-semibold mb-2 sale-product-name">
                              {{ product.name }}
                            </h3>
                            <div class="d-flex align-items-baseline flex-wrap mt-3" v-if="$root.logged && product.distributor">
                              <h6 class="title-distributor"><i class="fa-regular fa-box mr-1 ic"></i> Distributor price</h6>
                              <span class="h4 font-weight-bold text-danger mr-1">
                                ${{ Intl.NumberFormat("en-US", { minimumFractionDigits: 2 }).format(product.distributor_unit_price) }}
                              </span>
                              <span class="usd text-danger">USD</span>
                              <span class="small text-muted w-100">1 piece</span>
                              <p
                                class="small wholesale-distributor mb-0 w-100"
                                v-if="product.wholesale_price && product.wholesale_quantity"
                              >
                                Wholesale: ${{ Intl.NumberFormat("en-US", { minimumFractionDigits: 2 }).format(product.wholesale_price) }} USD from {{ product.wholesale_quantity }} {{ product.wholesale_quantity === 1 ? 'piece' : 'pieces' }}
                              </p>
                            </div>
                            <div class="d-flex align-items-baseline flex-wrap mt-3" v-else-if="$root.logged">
                              <span class="h4 font-weight-bold mr-1" v-if="!product.price_offer">
                                ${{ Intl.NumberFormat("en-US", { minimumFractionDigits: 2 }).format(product.price) }}
                              </span>
                              <span class="h4 font-weight-bold text-danger mr-1" v-if="product.price_offer">
                                ${{ Intl.NumberFormat("en-US", { minimumFractionDigits: 2 }).format(product.price_offer) }}
                              </span>
                              <span class="usd" :class="{ 'text-danger': product.price_offer }">USD</span>
                            </div>
                            <p class="small mt-1 mb-0 sku">{{ product.sku }}</p>
                          </div>
                          <div class="card-footer bg-white border-top">
                            <a class="btn btn-accent btn-block" to="">
                              READ
                              <!-- {{ product.inStock ? 'Add to cart' : 'Out of stock' }} -->
                            </a>
                          </div>
                        </div>
                      </router-link>
                    </div>
                  </swiper-slide>

                  <div class="swiper-button-prev" slot="button-prev"></div>
                  <div class="swiper-button-next" slot="button-next"></div>
                </swiper>
              </div>
            </div>
          </div>
        </section>
      </template>
    </main>
  </div>
</template>

<script>
export default {
  data() {
    return {
      isLoading: true,
      id: '',
      error: false,
      product: {
        images: [],
        price: 0,
        price_offer: 0,
        name: '',
        sku: '',
        oem_cross_reference: '',
        description: '',
        short_description: '',
        engine_application: '',
        brand: '',
        category: '',
      },

      relatedProducts: [],

      // == Swiper options ==
      relatedProductsOptions: {
        slidesPerView: 3,
        spaceBetween: 30,

        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev'
        },

        breakpoints: {
          1200: {
            slidesPerView: 4,
            spaceBetween: 30
          },
          992: {
            slidesPerView: 3,
            spaceBetween: 30
          },
          768: {
            slidesPerView: 2,
            spaceBetween: 30
          },
          1: {
            slidesPerView: 1,
            spaceBetween: 30
          },
        }
      },
      // == ==
    }
  },

  methods: {
    getRelatedProducts() {
      axios.get(tools.url('/api/product/related/'+this.id)).then((response) => {
        this.relatedProducts = response.data;
      })
      .catch((error) => {
        console.log(error);
      });
    },
    
    getProduct() {
      this.isLoading = true;
      axios.get(tools.url('/api/product/'+this.id)).then((response) => {
        this.product = response.data;
        this.getRelatedProducts();
        this.isLoading = false;
      })
      .catch((error) => {
        this.isLoading = false;
        this.error = true;
        console.log(error);
      });
    },
  },

  mounted() {
    this.id = this.$root._getURLID(this.$route.params.id);

    this.getProduct();
  },

  watch: {
    "$route.params.id": function(v) {
      this.id = this.$root._getURLID(this.$route.params.id);
      this.getProduct();
    },
  },
}
</script>

<style scoped>
.related-product-card {
  transition: box-shadow 0.2s;
}
.related-product-card:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}
.related-product-img {
  object-fit: cover;
  transition: transform 0.3s;
}
.related-product-card:hover .related-product-img {
  transform: scale(1.05);
}
</style>
